import MapboxDraw, { MapMouseEvent } from '@mapbox/mapbox-gl-draw';
import { length } from '@turf/length';
import { GeometryCollection, Point, Polygon } from 'geojson';
import { FeatureProperties, MapModes } from '../constants';
import { createCircleFromPoint, getRadiusDescription } from '../utils';

const DrawCircle = { ...MapboxDraw.modes.draw_polygon };

class CircleState {
    polygon: any; // DrawFeature
    line: any; // DrawFeature
    currentVertexPosition: number;
    centerCoordinates: number[];
    isDragging: boolean;
}

DrawCircle.onSetup = function (opts: { featureId?: any }) {
    const polygon = this.newFeature({
        type: MapboxDraw.constants.geojsonTypes.FEATURE,
        properties: {
            meta: FeatureProperties.CIRCLE_POLYGON,
            customDrawMode: MapModes.DRAW_CIRCLE_SELECT
        },
        geometry: {
            type: MapboxDraw.constants.geojsonTypes.POLYGON,
            coordinates: [[]]
        }
    });
    this.addFeature(polygon);

    const line = this.newFeature({
        type: 'Feature',
        properties: {
            meta: FeatureProperties.CIRCLE_RADIUS_LINE,
            measurement: ``
        },
        geometry: {
            type: 'LineString',
            coordinates: []
        }
    });
    this.addFeature(line);

    this.clearSelectedFeatures();

    return {
        polygon,
        line,
        currentVertexPosition: 0,
        centerCoordinates: undefined,
        isDragging: false
    };
};

DrawCircle.onDrag = function (state: CircleState, e: MapMouseEvent) {
    state.isDragging = true;
    this.onMouseMove(state, e);
};

DrawCircle.onMouseUp = function (
    state: CircleState,
    e: { lngLat: { lng: any; lat: any } }
) {
    if (state.isDragging) {
        return this.changeMode(MapModes.SIMPLE_SELECT as MapboxDraw.DrawMode);
    }
};

DrawCircle.onMouseMove = function (
    state: CircleState,
    e: { lngLat: { lng: any; lat: any } }
) {
    if (state.currentVertexPosition === 1) {
        state.line.updateCoordinate(
            state.currentVertexPosition,
            e.lngLat.lng,
            e.lngLat.lat
        );

        const lineGeoJson = state.line.toGeoJSON();
        const km = length(lineGeoJson);
        const radiusMetres = Math.max(1, Math.round(km * 1000));

        const ring = createCircleFromPoint(state.centerCoordinates, radiusMetres);
        state.polygon.incomingCoords(ring.geometry.coordinates);
        state.polygon.properties.radiusMetres = radiusMetres;

        state.line.properties.measurement = getRadiusDescription(radiusMetres);
    }
};

DrawCircle.toDisplayFeatures = function (
    state: CircleState,
    geojson: any,
    display: (arg0: any) => void
) {
    // Until the centre is clicked neither feature has positions, and gl-draw
    // closes the empty ring as [[undefined]], which the draw source rejects
    // as invalid GeoJSON
    const positions =
        geojson.geometry.type === 'Polygon'
            ? geojson.geometry.coordinates[0]
            : geojson.geometry.coordinates;
    if (positions == null || positions.length < 2 || positions[0] == null) {
        return;
    }
    display(geojson);
};

// @ts-ignore: not in the current type
DrawCircle.clickAnywhere = function (
    state: CircleState,
    e: { lngLat: { lng: any; lat: any } }
) {
    if (state.currentVertexPosition === 0) {
        state.currentVertexPosition++;
        const center = [e.lngLat.lng, e.lngLat.lat];
        state.centerCoordinates = center;

        state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
        state.line.addCoordinate(1, e.lngLat.lng, e.lngLat.lat);
    } else if (state.currentVertexPosition === 1) {
        return this.changeMode(MapModes.SIMPLE_SELECT);
    }
};

DrawCircle.onStop = function (state: CircleState) {
    this.activateUIButton();
    // Escape is handled by the inherited draw_polygon.onKeyUp, which deletes
    // only the polygon; either feature missing means the draw was cancelled
    const polygonExists = this.getFeature(state.polygon.id) !== undefined;
    const lineExists = this.getFeature(state.line.id) !== undefined;
    if (!polygonExists || !lineExists) {
        if (lineExists) {
            this.deleteFeature(state.line.id, { silent: true });
        }
        if (polygonExists) {
            this.deleteFeature(state.polygon.id, { silent: true });
        }
        return;
    }

    if (state.centerCoordinates != null && state.polygon.isValid()) {
        this.map.fire(MapboxDraw.constants.events.CREATE, {
            features: [
                buildCircleFeature(
                    { type: 'Point', coordinates: state.centerCoordinates },
                    { radiusMetres: state.polygon.properties.radiusMetres }
                )
            ]
        });
    } else {
        this.deleteFeature(state.line.id, {
            silent: true
        });
        this.deleteFeature(state.polygon.id, {
            silent: true
        });
        this.changeMode(MapModes.SIMPLE_SELECT as MapboxDraw.DrawMode, {}, { silent: true });
    }
};

export function buildCircleFeature(point: Point, properties: { radiusMetres?: number }) {
    const radiusMetres = properties.radiusMetres ?? 500;
    const ring = createCircleFromPoint(point.coordinates, radiusMetres);
    return {
        type: 'Feature',
        properties: {
            radiusMetres
        },
        geometry: {
            type: 'GeometryCollection',
            geometries: [ring.geometry, point]
        } as GeometryCollection<Polygon | Point>
    }
}

export default DrawCircle;
